import React, { useCallback, useMemo } from 'react';
import { Form, Input, Modal, Button, Switch, Tree } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { TreeDataNode } from 'antd';
import { useCampaignsListWithFoldersQuery } from '@hooks/queries/campaign';
import {
  toggleCampaignFolderCreationModal,
  toggleCopyModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { useCopyFolderMutation } from '@hooks/queries/assets';

import './CopyModal.less';

type FormValues = {
  searchText: string;
  selectedCampaignId: string;
  copyAssets: boolean;
  copyComments: boolean;
  includeMembers: boolean;
};

type CopyModalFolderProps = {
  visible: boolean;
  folderId: string;
};

export default function CopyModalFolder(props: CopyModalFolderProps) {
  const { visible, folderId } = props;
  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();
  const canCreateCampaign = currentWorkspace?.permissions?.createCampaigns;

  const searchText = Form.useWatch('searchText', form) ?? '';

  const { data: campaignsListQuery } = useCampaignsListWithFoldersQuery({
    params: {
      workspaceId: currentWorkspace?.id || ''
    }
  });

  const campaignsList: TreeDataNode[] = useMemo(() => {
    let _campaignsList = campaignsListQuery ?? [];
    if (searchText) {
      _campaignsList = _campaignsList.filter((campaign) => {
        return campaign.name
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      });
    }
    return _campaignsList.map((item) => ({
      key: item.id,
      title: item.name
    }));
  }, [campaignsListQuery, searchText]);

  const { mutateAsync: copyFolder, isPending: isCopyingFolder } =
    useCopyFolderMutation({});

  const onCloseModal = useCallback(() => {
    dispatch(toggleCopyModal(null));
  }, [dispatch]);

  const onCopyFolder = (data: FormValues) => {
    copyFolder(
      {
        workspaceId: currentWorkspace?.id || '',
        folderId,
        targetCampaignId: data.selectedCampaignId,
        copyAssets: data.copyAssets,
        copyComments: data.copyComments,
        includeMembers: data.includeMembers
      },
      {
        onSuccess: (res) => {
          if (res.data.jobId) {
            localStorage.setItem(
              `copy-link-${res.data.jobId}`,
              `/campaigns/single/${data.selectedCampaignId}/${res.data.newFolderId}`
            );
          }
          onCloseModal();
        }
      }
    );
  };

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        notShowCampaignCreatedModal: true
      })
    );
  }, [dispatch, canCreateCampaign]);

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossSvg />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onCopyFolder}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          copyAssets: true,
          copyComments: true,
          includeMembers: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 24 }}
        >
          Copy to
        </h2>
        <Form.Item name="searchText">
          <Input
            placeholder="Search by project"
            prefix={<SearchSvg />}
            size="large"
          />
        </Form.Item>
        <div className="copy-modal__tree-wrapper">
          <Tree
            treeData={campaignsList}
            showIcon={true}
            switcherIcon={<ArrowDownSvg width={20} height={20} />}
            onSelect={(_selectedKeys, { node }) => {
              form.setFieldsValue({
                selectedCampaignId: node.selected ? '' : (node.key as string)
              });
            }}
          />
          {canCreateCampaign && (
            <div
              className="copy-modal__action-button"
              onClick={onCreateProject}
            >
              <PlusSvg />
              New project
            </div>
          )}
        </div>
        <Form.Item name="selectedCampaignId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item
          name="copyAssets"
          valuePropName="checked"
          label="Duplicate media"
          className="duplicate-project-modal__switch"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="copyComments"
          valuePropName="checked"
          label="Duplicate comments"
          className="duplicate-project-modal__switch"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="includeMembers"
          valuePropName="checked"
          label="Include members"
          className="duplicate-project-modal__switch"
        >
          <Switch />
        </Form.Item>
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isCopyingFolder}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Copy folder
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
